import React, { useState, useEffect, useRef } from "react";
import { Outlet, useNavigate, Link, useLocation } from "react-router-dom";
import { useBranding } from "../context/BrandingContext";
import {
  canAccessBranding,
  canAccessContentAdmin,
  canAccessSubscriptionAdmin,
  canAccessBabyNamesAdmin,
  canAccessStoreAdmin
} from "../utils/staffRoles";
import { useAuthGuard } from "../hooks/useAuthGuard";
import { ROUTES } from "../routes/paths";
import "./AdminLayout.css";
import { clearAuthSession } from "../utils/authSession";

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const role = localStorage.getItem("userRole") || "user";
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { branding } = useBranding();
  const skipUnauthorizedRedirectRef = useRef(false);
  const { authorized } = useAuthGuard({ requireStaff: true, skipRedirectRef: skipUnauthorizedRedirectRef });

  useEffect(() => {
    if (role === "editor" && (location.pathname === ROUTES.ADMIN || location.pathname === `${ROUTES.ADMIN}/`)) {
      navigate(ROUTES.ADMIN_CONTENT, { replace: true });
    }
  }, [role, location.pathname, navigate]);

  const handleLogout = () => {
    skipUnauthorizedRedirectRef.current = true;
    clearAuthSession();
    navigate(ROUTES.HOME, { replace: true });
  };

  const closeSidebar = () => setSidebarOpen(false);

  if (!authorized) {
    return null;
  }

  const showDashboard = role !== "editor";
  const showUsers = role === "admin" || role === "manager" || role === "moderator";
  const showAppointments = showUsers;
  const showReports = showUsers;
  const showContent = canAccessContentAdmin(role);
  const showPregnancy = canAccessContentAdmin(role);
  const showBranding = canAccessBranding(role);
  const showSubscription = canAccessSubscriptionAdmin(role);
  const showBabyNames = canAccessBabyNamesAdmin(role);
  const showStore = canAccessStoreAdmin(role);

  return (
    <div className="mkc-admin-layout">
      <button
        type="button"
        className="mkc-admin-toggle"
        aria-label="Toggle sidebar"
        onClick={() => setSidebarOpen((o) => !o)}
      >
        <span className="mkc-admin-toggle-bar" />
        <span className="mkc-admin-toggle-bar" />
        <span className="mkc-admin-toggle-bar" />
      </button>
      {sidebarOpen && <div className="mkc-admin-overlay" onClick={closeSidebar} aria-hidden="true" />}
      <aside className={`mkc-admin-sidebar ${sidebarOpen ? "mkc-admin-sidebar-open" : ""}`}>
        <div className="mkc-admin-brand">{branding.app_name} Admin</div>
        <nav className="mkc-admin-nav">
          {showDashboard && (
            <Link to={ROUTES.ADMIN} onClick={closeSidebar}>
              Dashboard
            </Link>
          )}
          {showUsers && (
            <Link to={ROUTES.ADMIN_USERS} onClick={closeSidebar}>
              Users
            </Link>
          )}
          {showContent && (
            <Link to={ROUTES.ADMIN_CONTENT} onClick={closeSidebar}>
              Content
            </Link>
          )}
          {showAppointments && (
            <Link to={ROUTES.ADMIN_APPOINTMENTS} onClick={closeSidebar}>
              Appointments
            </Link>
          )}
          {showReports && (
            <Link to={ROUTES.ADMIN_REPORTS} onClick={closeSidebar}>
              Reports
            </Link>
          )}
          {showPregnancy && (
            <Link to={ROUTES.ADMIN_PREGNANCY_WEEKS} onClick={closeSidebar}>
              Pregnancy Weeks
            </Link>
          )}
          {showSubscription && (
            <Link to={ROUTES.ADMIN_SUBSCRIPTION} onClick={closeSidebar}>
              Subscription
            </Link>
          )}
          {showSubscription && (
            <Link to={ROUTES.ADMIN_PAYMENTS} onClick={closeSidebar}>
              Payment log
            </Link>
          )}
          {showBabyNames && (
            <Link to={ROUTES.ADMIN_BABY_NAMES} onClick={closeSidebar}>
              Baby Names
            </Link>
          )}
          {showBabyNames && (
            <Link to={ROUTES.ADMIN_BABY_NAME_ATTRIBUTES} onClick={closeSidebar}>
              Name Attributes
            </Link>
          )}
          {showStore && (
            <Link to={ROUTES.ADMIN_STORE} onClick={closeSidebar}>
              Essential Store
            </Link>
          )}
          {showBranding && (
            <Link to={ROUTES.ADMIN_BRANDING} onClick={closeSidebar}>
              Branding
            </Link>
          )}
        </nav>
        <div className="mkc-admin-footer">
          <Link to={ROUTES.SUBSCRIPTION} onClick={closeSidebar}>
            Plans & coupons (app)
          </Link>
          <Link to={ROUTES.DASHBOARD} onClick={closeSidebar}>
            ← Back to App
          </Link>
          <button type="button" className="mkc-nav-logout" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </aside>
      <main className="mkc-admin-main">
        <Outlet />
      </main>
    </div>
  );
};

export default AdminLayout;
